// « Lignes d'un compte » — the drill-down from the balance (docs/PMS-LEDGER-UI-BRIEF.md §2.1).
//
// The lines of one account over the month shown on the balance, newest first, each with its invoice number
// (which opens the document). When EVERY line is loaded they are added up and compared with the account's
// own debit and credit on the balance: they must agree, and if they do not it is a defect shown in red.
// A partial list is never compared (« n sur N », « Charger la suite »).

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatAmount,
  formatDay,
  journalLabel,
  linesCheck,
  monthRange,
  type JournalLine,
  type LedgerReader,
  type TrialBalanceLine,
} from '@lemontaigu/fin-core';
import { Document } from './Document.js';

const PAGE = 200;

export function LignesView({
  account,
  lines,
  totalLines,
  onPiece,
  onMore,
  loadingMore,
  onClose,
}: {
  account: TrialBalanceLine;
  lines: readonly JournalLine[];
  totalLines: number;
  onPiece: (piece: string) => void;
  onMore: () => void;
  loadingMore: boolean;
  onClose: () => void;
}) {
  const check = linesCheck(lines, totalLines, { debitCents: account.debitCents, creditCents: account.creditCents });
  // A revenue account has no customer and no label on its lines: do not spend two empty columns on them.
  const withHolder = lines.some((l) => l.holderRef);
  const withLabel = lines.some((l) => l.label);
  return (
    <section className="fin-lignes" data-testid="fin-lignes" aria-label={`Lignes du compte ${account.account}`}>
      <div className="fin-mouvements-head">
        <h4>{account.account} <span className="muted">{account.label}</span></h4>
        <button type="button" onClick={onClose}>Fermer</button>
      </div>
      {lines.length === 0 ? (
        <p className="muted" data-testid="fin-lignes-empty">Aucune ligne sur ce compte pour ce mois.</p>
      ) : (
        <div className="pms-table-wrap">
          <table className="pms-table">
            <thead>
              <tr>
                <th>Date</th><th>Journal</th><th>Pièce</th>{withHolder && <th>Client</th>}{withLabel && <th>Libellé</th>}
                <th className="fin-num">Débit</th><th className="fin-num">Crédit</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.lineId}>
                  <td>{formatDay(l.entryDate)}</td>
                  <td>{journalLabel(l.journal)}</td>
                  <td>
                    <button type="button" className="fin-link" onClick={() => onPiece(l.pieceRef)}>{l.pieceRef}</button>
                  </td>
                  {withHolder && <td>{l.holderRef ?? ''}</td>}
                  {withLabel && <td>{l.label ?? ''}</td>}
                  <td className="fin-num">{l.debitCents === 0 ? '' : formatAmount(l.debitCents)}</td>
                  <td className="fin-num">{l.creditCents === 0 ? '' : formatAmount(l.creditCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted" data-testid="fin-lignes-count">
        {lines.length} sur {totalLines} ligne(s)
        {check.agrees === null && ' : la somme n’est comparée à la balance que lorsque toutes les lignes sont chargées.'}
      </p>
      {check.agrees === true && (
        <p className="fin-agree" data-testid="fin-lignes-ok">
          Les lignes somment au compte : débit {formatAmount(check.debitCents)}, crédit {formatAmount(check.creditCents)}, comme sur la balance.
        </p>
      )}
      {check.agrees === false && (
        <p className="fin-health fin-health--alert" role="alert" data-testid="fin-lignes-defect">
          Les lignes ne somment pas au compte : lignes {formatAmount(check.debitCents)} / {formatAmount(check.creditCents)} contre balance{' '}
          {formatAmount(account.debitCents)} / {formatAmount(account.creditCents)}. C’est un défaut de lecture ou du livre, pas une présentation.
        </p>
      )}
      {check.agrees === null && (
        <button type="button" onClick={onMore} disabled={loadingMore}>{loadingMore ? 'Chargement…' : 'Charger la suite'}</button>
      )}
    </section>
  );
}

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; lines: readonly JournalLine[]; totalLines: number }
  | { phase: 'forbidden' }
  | { phase: 'error'; message: string };

export function LignesCompte({
  ledger,
  book,
  month,
  account,
  onClose,
}: {
  ledger: LedgerReader;
  book: string;
  month: string;
  account: TrialBalanceLine;
  onClose: () => void;
}) {
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);
  const [piece, setPiece] = useState<string | null>(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    const mine = ++ticket.current;
    setState({ phase: 'loading' });
    setPiece(null);
    const { from, to } = monthRange(month);
    const r = await ledger.journalLines(book, { from, to, account: account.account, limit: PAGE });
    if (mine !== ticket.current) return;
    if (r.ok) setState({ phase: 'ready', lines: r.value.rows, totalLines: r.value.totalLines });
    else if (r.error.kind === 'forbidden') setState({ phase: 'forbidden' });
    else setState({ phase: 'error', message: r.error.message });
  }, [ledger, book, month, account.account]);

  useEffect(() => {
    void load();
  }, [load]);

  const more = async () => {
    if (state.phase !== 'ready' || state.lines.length === 0) return;
    const mine = ticket.current;
    setLoadingMore(true);
    const { from, to } = monthRange(month);
    const before = state.lines[state.lines.length - 1]!.lineId;
    const r = await ledger.journalLines(book, { from, to, account: account.account, limit: PAGE, before });
    setLoadingMore(false);
    if (mine !== ticket.current) return;
    if (r.ok) setState({ phase: 'ready', lines: [...state.lines, ...r.value.rows], totalLines: r.value.totalLines });
    else if (r.error.kind === 'forbidden') setState({ phase: 'forbidden' });
    else setState({ phase: 'error', message: r.error.message });
  };

  if (state.phase === 'loading') return <p className="muted" data-testid="fin-lignes-loading">Chargement des lignes du compte {account.account}…</p>;
  if (state.phase === 'forbidden') {
    return (
      <div className="pms-warn" role="alert" data-testid="fin-lignes-forbidden">
        Droit manquant : les lignes d’un compte sont réservées à l’administrateur.
        <button type="button" onClick={onClose}>Fermer</button>
      </div>
    );
  }
  if (state.phase === 'error') {
    return (
      <div className="pms-warn" role="alert" data-testid="fin-lignes-error">
        <p>Les lignes du compte {account.account} sont illisibles : ne rien en conclure. ({state.message})</p>
        <button type="button" onClick={() => void load()}>Réessayer</button>
        <button type="button" onClick={onClose}>Fermer</button>
      </div>
    );
  }
  return (
    <>
      <LignesView
        account={account}
        lines={state.lines}
        totalLines={state.totalLines}
        onPiece={setPiece}
        onMore={() => void more()}
        loadingMore={loadingMore}
        onClose={onClose}
      />
      {piece && <Document ledger={ledger} book={book} piece={piece} onClose={() => setPiece(null)} />}
    </>
  );
}
